import { Injectable } from "@angular/core";
import { FetchStatusSelector } from "@obg/ngrx-utils";
import { memoizeSelector } from "@obg/ngx.global";
import { find, isEmpty, isEqual } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";

import { CoreState } from "../state";

import { ACTION_PREFIX, DEFAULT_TRANSACTIONTOTALS_ID } from "./wallet.action";
import {
	AamsBalance,
	AamsBalances,
	Balance,
	Balances,
	BetWinSummary,
	BonusContext,
	BonusContextBalance,
	Transaction,
	TransactionDetail,
	TransactionTotals,
	WithdrawalRestrictions,
} from "./wallet.model";
/**
 * @deprecated [fabric-deprecated] - Use wallet-service from app-wallet instead
 */
// @dynamic
@Injectable({
	providedIn: "root",
})
export class WalletSelector {
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetWalletBalance instead
	 */
	getBalance() {
		return (state: CoreState): Balance | AamsBalance | undefined =>
			state.wallet.aamsBalance || state.wallet.balance;
	}
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetWalletBalance instead
	 */
	getBalance$() {
		return (state$: Observable<CoreState>): Observable<Balance | AamsBalance> =>
			state$.pipe(
				map(this.getBalance()),
				filter((balance) => !!balance),
				distinctUntilChanged(isEqual)
			);
	}

	getTotalAmount() {
		return (state: CoreState): number | undefined =>
			(state.wallet.balance && state.wallet.balance.totalAmount) ||
			(state.wallet.aamsBalance && state.wallet.aamsBalance.totalAmount);
	}

	getBetWinSummary() {
		return (state: CoreState): BetWinSummary | undefined => state.wallet.betWinSummary;
	}

	getTransaction() {
		return (state: CoreState): TransactionDetail | undefined => state.wallet.transaction;
	}
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetTransaction instead
	 */
	getTransactions() {
		return (state: CoreState): Transaction | undefined => state.wallet.transactions;
	}
	/**
	 * @deprecated Use getTransactionTotals2
	 */
	getTransactionTotals() {
		return this.getTransactionTotals2(DEFAULT_TRANSACTIONTOTALS_ID);
	}
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetTransactionTotals instead
	 */
	getTransactionTotals2(id: string) {
		return (state: CoreState): TransactionTotals | undefined =>
			state.wallet.transactionTotals && state.wallet.transactionTotals[id];
	}

	@memoizeSelector<CoreState>((state) => [state.wallet.bonusContextBalances])
	getBonusContextBalance(context: BonusContext) {
		return (state: CoreState): BonusContextBalance | undefined =>
			find(state.wallet.bonusContextBalances, (x) => x.contextName === context);
	}

	getPlayableAmountByContext(contextName: BonusContext) {
		return (state: CoreState): number => {
			const balance = this.getBonusContextBalance(contextName)(state);
			return balance?.playableAmount ?? 0;
		};
	}

	isBusy() {
		return (state: CoreState): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);
	}

	getBalances$() {
		return (state$: Observable<CoreState>): Observable<Balances | AamsBalances> =>
			state$.pipe(
				map((state) =>
					"aamsBalance" in state.wallet
						? {
								balance: state.wallet.aamsBalance,
								bonusContextBalances: state.wallet.aamsBonusContextBalances,
							}
						: {
								balance: state.wallet.balance,
								bonusContextBalances: state.wallet.bonusContextBalances,
							}
				),
				distinctUntilChanged(isEqual)
			);
	}

	getCurrentProductBalance() {
		return (state: CoreState): Balance | undefined => state.wallet.currentProductBalance;
	}

	getCurrentProductBalance$() {
		return (state$: Observable<CoreState>): Observable<Balance> =>
			state$.pipe(
				map(this.getCurrentProductBalance()),
				filter((balance) => !isEmpty(balance)),
				distinctUntilChanged(isEqual)
			);
	}

	getWithdrawalRestriction() {
		return (state: CoreState): WithdrawalRestrictions | undefined => state.wallet.withdrawalRestriction;
	}
}
